import { TrackReferenceOrPlaceholder } from '@livekit/components-react'
import { Room } from 'livekit-client'
import Tile from './Tile'
import useMuseTracks, { MuseTracks } from './useMuseTracks'
import { useMemo } from 'react'

type Props = {
    room: Room
    hostUserId: string
}

const MusiversalGridLayout = ({ room, hostUserId }: Props) => {
    const museTracks = useMuseTracks(room, hostUserId)

    const count = museTracks.all.length
    if (count === 1) {
        return <Tile trackRef={museTracks.all[0]} className="h-screen w-screen" />
    } else if (count === 2) {
        return (
            <div className="flex h-screen items-center">
                <Tile trackRef={museTracks.all[0]} className="w-[50vw] max-h-screen" />
                <Tile trackRef={museTracks.all[1]} className="w-[50vw] max-h-screen" />
            </div>
        )
    } else if (count === 3) {
        return <Grid3 museTracks={museTracks} />
    } else {
        return <Grid4 museTracks={museTracks} />
    }
}

function Grid3({ museTracks }: { museTracks: MuseTracks }) {
    const [top, left, right] = useMemo(() => {
        const top = museTracks.screen ?? museTracks.all[0]
        const [left, right] = museTracks.all.filter((t) => t !== top)
        return [top, left, right]
    }, [museTracks.all])

    return (
        <div className="w-screen h-screen">
            <div className="flex justify-center h-[50vh]">
                <Tile trackRef={top} />
            </div>
            <div className="flex justify-center h-[50vh] w-screen">
                <Tile trackRef={left} className="h-[50vh]" />
                <Tile trackRef={right} className="h-[50vh]" />
            </div>
        </div>
    )
}

function Grid4({ museTracks }: { museTracks: MuseTracks }) {
    const [host, screen, p1, p2, rest] = useMemo(() => {
        const screen = museTracks.screen ?? museTracks.all[0]
        const host = museTracks.host ?? museTracks.all.find((t) => t !== screen)
        const rest = museTracks.all.filter((t) => t !== host && t !== screen)
        const [p1, p2] = rest.slice(0, 2)

        return [host, screen, p1, p2, rest]
    }, [museTracks.all])

    const smallVideos = rest.slice(2, 7).map((t: TrackReferenceOrPlaceholder) => {
        return (
            <Tile
                key={t.publication?.trackSid ?? t.participant.sid}
                trackRef={t}
                className="rounded-lg border-2 border-white"
            />
        )
    })

    return (
        <div className="relative flex flex-wrap">
            <Tile trackRef={host} className="h-[50vh] w-[50vw]" />
            <Tile trackRef={screen} className="h-[50vh] w-[50vw]" />
            <Tile trackRef={p1} className="h-[50vh] w-[50vw]" />
            <Tile trackRef={p2} className="h-[50vh] w-[50vw]" />
            <div className="absolute top-2 left-2 z-10 w-[15vw] flex flex-col gap-2">{smallVideos}</div>
        </div>
    )
}

export default MusiversalGridLayout
